import loadable from '@loadable/component';
import { toPercentage, toCurrency } from '@/utilities/formatting';
import DataTable from '@/components/DataTable';
import { TableWrapper } from './styled';
import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import ReportSectionHeader from '@/components/ReportSectionHeader';
import {
  PORTFOLIO_STATIC_COMPANY,
  PORTFOLIO_DIVIDEND_PAYERS,
} from '@/constants/feedbackSections';
import type { HoldingDividend } from '@/components/DeprecatedPortfolio/components/Dividend/redux/interface';

const ChartActionsContainer = loadable(
  () => import('@/components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);

export interface DividendProps {
  sectionNumber: number;
  currentDividend?: number;
  totalValue: number | null;
  currencyISO: string;
  isWatchlist: boolean;
  dividendHoldings: readonly HoldingDividend[];
}

const DividendDetails = ({
  sectionNumber,
  currentDividend,
  totalValue,
  isWatchlist,
  dividendHoldings,
  currencyISO,
}: DividendProps) => (
  <ReportSection sectionNumber={sectionNumber}>
    <ReportSectionHeader
      title="Dividends"
      sectionDescription="The average annual dividend yield and top payers"
      keyMetric={
        currentDividend && currentDividend > 0
          ? toPercentage(currentDividend / 100, 2)
          : ''
      }
      keyMetricTitle={
        isWatchlist && currentDividend && currentDividend > 0
          ? 'Estimated Dividend Yield'
          : !isWatchlist && currentDividend && currentDividend > 0
            ? `Equivalent to ${toCurrency(
                (currentDividend / 100) * (totalValue || 0),
                0,
                currencyISO
              )} per year`
            : ''
      }
    />
    {dividendHoldings.length > 0 ? (
      <ReportSubSection
        title="List of Dividend Payers"
        trackingProps={{ subject: 'chart' }}
        body={
          <>
            <TableWrapper>
              <DataTable
                columnWidths={!isWatchlist ? `2,1,1` : `1,1`}
                columnTextAlignments={!isWatchlist ? `l,l,l` : `l,r`}
              >
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Annual Yield</th>
                    {!isWatchlist && <th>Annual Contribution</th>}
                  </tr>
                </thead>
                <tbody>
                  {dividendHoldings.map((n) => (
                    <tr key={n.companyId}>
                      <td>{n.companyName}</td>
                      <td>{toPercentage(n.currentDividend / 100, 1)}</td>
                      {!isWatchlist && (
                        <td>
                          {toCurrency(
                            (n.currentDividend / 100) * n.currentValue,
                            0,
                            currencyISO
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableWrapper>
            <ChartActionsContainer
              companyName={PORTFOLIO_STATIC_COMPANY}
              feedbackLabel={PORTFOLIO_DIVIDEND_PAYERS}
            />
          </>
        }
      />
    ) : null}
  </ReportSection>
);

export default DividendDetails;
